import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  PhoneNumberInput,
  TextArea,
  TextInput,
} from 'shared/components';
import { debouncedValidateJSON, validateJSON } from 'shared/validation';
import { patchLicenseUser } from 'licenseUsers/actions';
import './EditLicenseUserModal.scss';

const formatMetadataValue = (metadata) => {
  if (!metadata) {
    return '';
  }

  if (typeof metadata === 'string') {
    return metadata;
  }

  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return '';
  }
};

const EditLicenseUserModal = ({ closeCb, confirmCb, user, userID }) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);

  const [firstName, setFirstName] = useState(get(user, 'first_name') || '');
  const [lastName, setLastName] = useState(get(user, 'last_name') || '');
  const [phoneNumber, setPhoneNumber] = useState(get(user, 'phone_number') || '');
  const [metadata, setMetadata] = useState(
    formatMetadataValue(get(user, 'metadata')),
  );
  const [metadataError, setMetadataError] = useState('');

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }

    return setDirtyFormDisplay(true);
  };

  const validateForm = async () => {
    const metadataErr = await validateJSON(metadata);
    setMetadataError(metadataErr);

    return !metadataErr;
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (isLoading) {
      return false;
    }

    if (!isDirty) {
      closeCb();
      return true;
    }

    const isValid = await validateForm();
    if (!isValid) {
      return false;
    }

    setLoading(true);

    let parsedMetadata = {};
    if (metadata) {
      try {
        parsedMetadata = JSON.parse(metadata);
      } catch {
        setLoading(false);
        setMetadataError(t('Metadata must be valid JSON object'));
        return false;
      }
    }

    const data = {
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      metadata: parsedMetadata,
    };

    return patchLicenseUser(userID, data)
      .then(() => {
        setLoading(false);
        Notification('success', t('Changes saved successfully'));
        confirmCb();
        return true;
      })
      .catch(() => {
        setLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
        return false;
      });
  };

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={t('Edit license user')}
      size="md"
    >
      <form className="EditLicenseUserModal" onSubmit={handleSubmit}>
        <div className="form-row">
          <Label text={t('Email')} inputId="license-user-email" />
          <TextInput
            id="license-user-email"
            value={get(user, 'true_email') || ''}
            disabled
          />
        </div>

        <div className="form-row">
          <Label text={t('First Name')} inputId="license-user-first-name" />
          <TextInput
            id="license-user-first-name"
            value={firstName}
            handleChange={(val) => {
              setDirty(true);
              setFirstName(val);
            }}
          />
        </div>

        <div className="form-row">
          <Label text={t('Last Name')} inputId="license-user-last-name" />
          <TextInput
            id="license-user-last-name"
            value={lastName}
            handleChange={(val) => {
              setDirty(true);
              setLastName(val);
            }}
          />
        </div>

        <div className="form-row">
          <Label text={t('Phone Number')} inputId="license-user-phone-number" />
          <PhoneNumberInput
            value={phoneNumber}
            handleChange={(val) => {
              setDirty(true);
              setPhoneNumber(val || '');
            }}
          />
        </div>

        <div className="form-row">
          <Label text={t('Metadata JSON')} inputId="license-user-metadata" />
          <TextArea
            id="license-user-metadata"
            rows="4"
            value={metadata}
            error={metadataError}
            handleChange={(val) => {
              setDirty(true);
              setMetadata(val);
              debouncedValidateJSON(val).then((err) => setMetadataError(err));
            }}
          />
        </div>

        <button type="submit" style={{ display: 'none' }} />
      </form>

      {isDirtyFormDisplayed && (
        <DirtyFormAlert
          dirty={isDirty}
          closeAlert={() => setDirtyFormDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

EditLicenseUserModal.propTypes = {
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  userID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default EditLicenseUserModal;
